/**
 * `useFileAssociation` — resolves the file-association pair (forward +
 * reverse) for a given entity type by querying the live CRM schema.
 *
 * Exclusions applied when filtering candidates:
 *  - `source_entity_type_name !== 'ahoy_file'` — only edges that NAME the file
 *    type as their source are file associations. A wildcard-source edge
 *    (`source_entity_kind_name: any`, e.g. `ahoy_entity_to_creator` →
 *    principal-kind targets such as API keys) matches `ahoy_file` by kind but
 *    is not a file association, and must not conjure a Files section.
 *  - `value_write_access === 'internal'` — attaching writes the edge, so an
 *    edge users cannot write must not be offered.
 *  - `value_read_access !== 'all'` — internal-only associations are
 *    invisible to user-sourced reads and must not be offered.
 *  - `target_entity_type_name === 'ahoy_user'` / `'ahoy_org'` — the
 *    `ahoy_file_to_owner` association is type-based
 *    (`target_entity_type_name: 'ahoy_user'`) with no explicit
 *    `value_read_access`, so it defaults to `'all'`; these two named targets
 *    are blocked explicitly to prevent user/org targets from leaking
 *    through.
 *
 * When multiple candidates survive the filters, the winner is chosen
 * deterministically: type-based associations (non-empty
 * `target_entity_type_name`) beat kind-based ones (empty string), with ties
 * broken by lexicographic `association_type_name`.
 */
import { useMemo } from 'react';

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { logger } from '@/lib/logger';

import { useAssociationMatchers } from './useAssociationMatchers';

export interface FileAssociationPair {
  forward: string;
  reverse: string;
}

export interface UseFileAssociationResult {
  association: FileAssociationPair | null;
  isLoading: boolean;
}

export function useFileAssociation(
  orgId: number | undefined,
  entityTypeName: string
): UseFileAssociationResult {
  const { associationTypes, matchesSource, matchesTarget, isLoading } =
    useAssociationMatchers(orgId);

  return useMemo(() => {
    const candidates = associationTypes.filter(
      (at: AssociationType) =>
        // A FILE association names ahoy_file as its source. A wildcard-source
        // edge (`source_entity_kind_name: any`, e.g. ahoy_entity_to_creator)
        // also "matches" ahoy_file, but it is not about files at all — for a
        // principal-kind record it would surface a Files section whose attach
        // is refused by the engine.
        at.source_entity_type_name === 'ahoy_file' &&
        matchesSource(at, 'ahoy_file') &&
        matchesTarget(at, entityTypeName) &&
        at.value_read_access === 'all' &&
        // Attaching writes this edge; an internal-only edge cannot be offered.
        at.value_write_access !== 'internal' &&
        at.target_entity_type_name !== 'ahoy_user' &&
        at.target_entity_type_name !== 'ahoy_org'
    );

    if (candidates.length === 0) {
      return { association: null, isLoading };
    }

    if (candidates.length > 1) {
      logger.warn(
        'useFileAssociation: multiple file associations matched; picking deterministically',
        {
          entityTypeName,
          candidates: candidates.map(c => c.association_type_name),
        }
      );
    }

    const sorted = [...candidates].sort((a, b) => {
      const aTyped = a.target_entity_type_name ? 1 : 0;
      const bTyped = b.target_entity_type_name ? 1 : 0;
      if (bTyped !== aTyped) {
        return bTyped - aTyped;
      }
      return a.association_type_name.localeCompare(b.association_type_name);
    });

    const winner = sorted[0];
    return {
      association: {
        forward: winner.association_type_name,
        reverse: winner.reverse_association_type_name,
      },
      isLoading,
    };
  }, [
    associationTypes,
    matchesSource,
    matchesTarget,
    entityTypeName,
    isLoading,
  ]);
}
